import { Button, Modal, Table } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';
import { useWarningsSummaryFormLoaderData } from '../loaders/StudentWarningsSummaryFormLoader';
import { StudentWarningsSummaryRow } from '../types';

export type StudentWarningsSummaryWarningsModalMethods = {
  open: (row: StudentWarningsSummaryRow) => void;
  close: () => void;
};

type StudentWarningsSummaryWarningsModalProps = {
  onClose?: (row: StudentWarningsSummaryRow) => void;
};

const StudentWarningsSummaryWarningsModal = (
  { onClose }: StudentWarningsSummaryWarningsModalProps,
  ref: Ref<StudentWarningsSummaryWarningsModalMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_SUMMARY);
  const data = useWarningsSummaryFormLoaderData();

  const [selectedRow, setSelectedRow] = useState<StudentWarningsSummaryRow>();

  const items = useMemo(
    () =>
      data.warningGroups.map((group) => ({
        label: group.label,
        value:
          selectedRow?.warnings[
            group.value as keyof typeof selectedRow.warnings
          ] || 0,
      })),
    [data.warningGroups, selectedRow],
  );

  const handleOpen = useCallback((row: StudentWarningsSummaryRow) => {
    setSelectedRow(row);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedRow(undefined);
    if (selectedRow) {
      !!onClose && onClose(selectedRow);
    }
  }, [onClose, selectedRow]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal isOpen={!!selectedRow} toggle={handleClose}>
      <div className="d-flex flex-column align-items-center text-center">
        <h2 className="text-dark fs-20 mb-4">{t('warningsModal.title')}</h2>
        <div className="w-100">
          <Table
            data={items}
            columns={[
              {
                columnName: 'label',
                headerText: t('warningsModal.group'),
                tdClassName: 'text-left',
                thClassName: 'text-left',
                cellFormat: ({ row }) => <span>{row.label}</span>,
              },
              {
                columnName: 'value',
                headerText: t('warningsModal.warnings'),
                tdClassName: 'text-right',
                thClassName: 'text-right',
              },
            ]}
          />
        </div>
        <div className="w-100">
          <Button
            className="w-100"
            type="button"
            size="md"
            fullwidth
            outlined
            text={t('common:actions.close')}
            onClick={handleClose}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(StudentWarningsSummaryWarningsModal);
